import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import VisuallyHidden from 'components/consumer/VisuallyHidden';
import BrandTooltip from 'components/consumer/BrandTooltip';

const ToggleButton = ({
  contentId,
  id,
  isSelected,
  label,
  description,
  onClick,
  size = 'sm',
  styling,
  infoTooltip,
  infoTooltipContent,
  showCloseButton = true,
  iconSize = 'small',
}) => {
  const isImportantToggle = styling === 'ImportantToggle';

  return (
    <div
      className={classNames(
        'group/ToggleButton items-center flex justify-between relative',
        {
          'inline-flex': isImportantToggle,
        }
      )}
      key={id}
    >
      <button
        aria-controls={contentId}
        aria-selected={isSelected ? 'true' : 'false'}
        className={classNames(
          'items-center bg-white border-solid text-gray cursor-pointer flex flex-1 justify-center whitespace-nowrap',
          {
            // Default styling
            'border border-gray-light7': !isImportantToggle,
            'hover:text-gray hover:!bg-gray-light9':
              !isSelected && !isImportantToggle,
            'group-[:first-child]/ToggleButton:rounded-tl group-[:first-child]/ToggleButton:rounded-bl group-[:last-child]/ToggleButton:rounded-tr group-[:last-child]/ToggleButton:rounded-br': !isImportantToggle,
            'focus:outline-0': !isImportantToggle,
            'focus:!bg-gray-light9': !isSelected && !isImportantToggle,
            'focus-visible:[outline-style:auto] focus-visible:outline-2 focus-visible:outline-brand focus-visible:outline-offset-[-2px]': !isImportantToggle,
            'group-[:not(:first-child)]/ToggleButton:border-l-0 group-[:not(:first-child)]/ToggleButton:pl-[calc(.7rem_+_1px)]':
              !isSelected && !isImportantToggle,
            'group-[:not(:first-child):hover]/ToggleButton:border-l group-[:not(:first-child):hover]/ToggleButton:-ml-px':
              !isSelected && !isImportantToggle,

            // Default styling - selected
            'border border-brand !bg-brand text-white -ml-px pr-[calc(.7rem_+_1px)]':
              isSelected && !isImportantToggle,
            'focus-visible:[outline-style:auto] focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-[-4px]':
              isSelected && !isImportantToggle,
            'focus:!bg-brand-dark': isSelected && !isImportantToggle,
            'hover:!bg-brand-dark': isSelected && !isImportantToggle,

            // ImportantToggle styling
            'min-h-[48px] inline-block !py-4 !px-0': isImportantToggle,
            'border-b-2': isImportantToggle,
            'focus:border-brand-dark': isImportantToggle,
            'focus-visible:[outline-style:auto] focus-visible:outline-2 focus-visible:outline-brand': isImportantToggle,
            'border-white': !isSelected && isImportantToggle,

            // ImportantToggle styling - selected
            'border-brand-dark': isSelected && isImportantToggle,

            // Icon sizes
            '!m-0': iconSize === 'big',

            // Sizes
            'text-xs font-bold px-[.7rem] py-2': size === 'xs',
            'text-sm font-bold p-[.7rem]': size === 'sm',
          }
        )}
        id={id}
        onClick={onClick}
        role="tab"
        type="button"
      >
        <span
          className={classNames({
            'text-lg font-bold': isImportantToggle,
            'text-gray-light2': isImportantToggle && !isSelected,
            'text-gray': isImportantToggle && isSelected,
            'group-hover/ToggleButton:text-gray': isImportantToggle,
            'group-focus/ToggleButton:text-gray': isImportantToggle,
          })}
        >
          {label}
          {!!isSelected && <VisuallyHidden>- selected</VisuallyHidden>}
        </span>

        {description && !isImportantToggle && <span>{description}</span>}
      </button>

      <div
        className={classNames(
          'items-center cursor-pointer z-[1000] [&_svg]:!m-0',
          {
            '!top-[calc(50%_-_9px)]': iconSize === 'big',
          }
        )}
      >
        {infoTooltip && (
          <BrandTooltip
            iconClassName="!top-0"
            iconSize={iconSize}
            openOnHover
            showCloseButton={showCloseButton}
            title=""
            width={300}
          >
            {infoTooltipContent}
          </BrandTooltip>
        )}
      </div>
    </div>
  );
};

ToggleButton.propTypes = {
  contentId: PropTypes.string,
  id: PropTypes.string,
  isSelected: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['xs', 'sm']),
  styling: PropTypes.oneOf(['ImportantToggle']),
  infoTooltip: PropTypes.bool,
  infoTooltipContent: PropTypes.string,
  showCloseButton: PropTypes.bool,
  iconSize: PropTypes.string,
};

export default ToggleButton;
